import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  HelpCircle, Bot, Sparkles, Send, X, ExternalLink, ArrowRight,
  MessageSquare, Globe, HardDrive, Award, CreditCard, Rocket,
  RotateCcw, Check, ChevronRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const QUICK_PROMPTS = [
  "How does the 4-Column Conversations Inbox work?",
  "How do I create and publish a new Funnel?",
  "How do I connect Google Drive to Media Storage?",
  "Where do I issue Course Certificates with QR verification?",
  "How to connect Stripe and charge client payments?",
  "How do Autonomous AI Agents work?",
];

const PLATFORM_KNOWLEDGE = {
  inbox: {
    match: ["conversation", "inbox", "sms", "whatsapp", "message", "chat thread"],
    title: "Omnichannel Conversations & Team Inbox",
    route: "/inbox",
    routeLabel: "Go to Conversations",
    answer: `The **Conversations** module is built on an enterprise 4-column layout:
1. **Column 1 (Icon Rail)**: Quick access to Team Inbox, personal assignments, search, and workflows.
2. **Column 2 (Team Inbox)**: Filter by **Unread (108)**, **All**, **Recent**, or **Starred**. Select contacts like *C & L Construction* or *Oc Home Resources*.
3. **Column 3 (Chat Feed)**: View SMS, WhatsApp, and email history with date markers and retry links. Use the bottom composer to switch channels (💬 v) and draft replies with AI.
4. **Column 4 (Contact Details)**: Edit contact info (Name, Phone with US flag 🇺🇸, Email, DND status, Owners) and access quick actions (Notes, Tasks, Calendar, Invoices).`,
  },
  sites: {
    match: ["site", "funnel", "website", "landing page", "store", "webinar", "form", "survey"],
    title: "Sites, Funnels & Web Builder",
    route: "/sites",
    routeLabel: "Go to Sites & Funnels",
    answer: `The **Sites** module lets you manage conversion funnels, websites, and lead tools:
- **Funnels Tab**: Browse funnels like *New Landing (RP)* and *RealHUB Menu*.
- **✨ Build with AI**: Click this button in the top right to generate a complete landing page with headlines and booking steps in seconds.
- **+ New Funnel**: Manually set up a funnel name and custom URL slug.
- **Sub-Tabs**: Explore **Websites**, **Stores** (e-commerce), **Webinars**, **Chat Widget** (embed code for any external website), and **QR Codes**.`,
  },
  media: {
    match: ["media", "drive", "google drive", "storage", "asset", "upload", "photo", "video"],
    title: "Media Storage & Google Drive Sync",
    route: "/media",
    routeLabel: "Open Media Storage",
    answer: `The **Media Storage** hub centralizes your creative assets:
- **Connect Google Drive**: Click the **"Connect Google Drive"** button at the top right to link your Google Workspace / Gmail account.
- **Browse & Import**: Once connected, click **"Browse Drive →"** to explore remote Google Drive folders (*Campaign Graphics*, *Video Commercials*, *Brand Identity Kit*) and import files with one click into your library.
- **Library Assets**: Search, preview images/videos, copy public CDN URLs, and sync updates automatically.`,
  },
  courses: {
    match: ["course", "certificate", "membership", "drm", "verify", "qr code", "lesson", "portal"],
    title: "Memberships, DRM Video & 2-Year Certificates",
    route: "/memberships?tab=credentials",
    routeLabel: "Open Credentials & Certificates",
    answer: `The **Memberships** engine features:
- **Client Portal & DRM Player** (\`/portal/colibri-client\`): High-security Canvas video streaming with zero raw MP4 exposure, moving dynamic forensic watermarks, and anti-DevTools screen protection.
- **Knowledge Verification Test**: Users must pass an architectural quiz before marking any lesson complete.
- **Automatic 2-Year Verified Certificates**: Automatically issued on course completion with a scannable QR code resolving to \`/verify/:id\`.
- **Certificate Customizer Studio**: Customize templates (Modern Corporate, Academic Prestige, Minimal Obsidian, Executive Crest), brand colors, typography, logos, and faculty signatures.`,
  },
  payments: {
    match: ["payment", "stripe", "invoice", "billing", "subscription", "pricing", "card"],
    title: "Payments, Invoices & Stripe",
    route: "/payments",
    routeLabel: "Open Payments & Invoicing",
    answer: `The **Payments** center manages transactions:
- **Connect Stripe**: Connect your Stripe keys in App Marketplace or Launchpad for instant card payments.
- **Invoices & Estimates**: Generate professional PDF invoices with automated reminder follow-ups.
- **Subscriptions & Payment Links**: Sell recurring client retainer plans and send instant 1-click checkout links.`,
  },
  launchpad: {
    match: ["launchpad", "onboard", "setup", "start", "profile", "complete profile"],
    title: "Launchpad Onboarding Engine",
    route: "/launchpad",
    routeLabel: "Open Launchpad",
    answer: `The **Launchpad** tracks your real-world business setup:
1. Complete Business Profile & Operational Address
2. Connect Google Business Profile (for 5-star Google reviews)
3. Connect Google Drive to Media Storage
4. Connect Facebook & Instagram accounts
5. Install Web Chat Widget on your website
6. Connect Stripe for payments
7. Invite team members with role-based permissions

*Note: As soon as your setup reaches 100%, the header setup button disappears for a clean interface!*`,
  },
  referrals: {
    match: ["referral", "affiliate", "commission", "partner", "earn"],
    title: "Referral & Commission Program",
    route: "/referrals",
    routeLabel: "Open Referral Program",
    answer: `The **Referral Program** lets you earn monthly recurring revenue:
- **Your Referral Link**: Share your unique URL with 90-day cookie tracking.
- **Referred Clients Ledger**: Track active accounts (*The Grand Haven Hotel*, *Coastal Dental Studio*, etc.) and monthly recurring commissions ($375/mo, $300/mo).
- **Marketing Kit**: Pre-written promotional emails, LinkedIn posts, and agency pitch decks ready to copy and share.`,
  },
  reputation: {
    match: ["review", "reputation", "google review", "rating", "star", "negative review"],
    title: "Reputation & Review Maximizer",
    route: "/reputation",
    routeLabel: "Open Reputation Manager",
    answer: `The **Reputation** module manages your public brand presence:
- **Live Google Reviews**: Real-time customer ratings and sentiment analysis.
- **Flag Negative Reviews**: Take immediate action to dispute or address dissatisfied customers.
- **Daily Digest Agent**: Automated AI summary sent to the manager/owner at the end of each business day.
- **Review Request Campaigns**: Automated SMS and email nudges to recent clients for 5-star Google ratings.`,
  },
};

export default function PlatformGuideChatbot({ isOpen, onClose, onStartTour }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      sender: "bot",
      text: "👋 Hi! I'm your **Platform Guide Assistant**. I can help you with anything across LaunchDeck, RealHUB, and Colibri Studio — from setting up funnels and connecting Google Drive, to managing 4-column SMS conversations and issuing verified certificates. What can I help you with today?",
      route: null,
      routeLabel: null,
      time: "Just now",
    },
  ]);

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [isOpen, messages]);

  if (!isOpen) return null;

  const handleSend = async (userText) => {
    const textToSend = userText || input;
    if (!textToSend.trim() || busy) return;

    const userMsg = {
      id: "u-" + Date.now(),
      sender: "user",
      text: textToSend.trim(),
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };

    setMessages(prev => [...prev, userMsg]);
    if (!userText) setInput("");
    setBusy(true);

    const queryLower = textToSend.toLowerCase();

    // Try finding best knowledge match
    let matchedKnowledge = null;
    for (const key of Object.keys(PLATFORM_KNOWLEDGE)) {
      const k = PLATFORM_KNOWLEDGE[key];
      if (k.match.some(m => queryLower.includes(m))) {
        matchedKnowledge = k;
        break;
      }
    }

    // Attempt AI backend endpoint or contextual fallback
    try {
      const { data } = await api.post("/ai/copilot", {
        prompt: `You are the friendly, expert Platform Guide for LaunchDeck & Colibri Studio. Answer the user's question clearly and concisely with direct platform guidance:\n\nUser Question: ${textToSend}`,
      });

      const botReply = {
        id: "b-" + Date.now(),
        sender: "bot",
        text: data?.reply || data?.answer || (matchedKnowledge ? matchedKnowledge.answer : "I can help you navigate any module in the platform. Explore **Conversations**, **Sites & Funnels**, **Media Storage**, **AI Agents**, or **Memberships** from the left navigation bar."),
        route: matchedKnowledge?.route || null,
        routeLabel: matchedKnowledge?.routeLabel || null,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      };

      setMessages(prev => [...prev, botReply]);
    } catch {
      // Instant intelligent fallback
      setTimeout(() => {
        const botReply = {
          id: "b-" + Date.now(),
          sender: "bot",
          text: matchedKnowledge
            ? matchedKnowledge.answer
            : `Here is what you can do on our platform:
- **Conversations** (\`/inbox\`): Omnichannel 4-column team inbox for SMS, Email, and WhatsApp.
- **Sites** (\`/sites\`): Create high-converting Funnels with AI, build Websites, and deploy Web Chat widgets.
- **Media Storage** (\`/media\`): Connect Google Drive to sync and import all your cloud assets.
- **Memberships** (\`/memberships\`): Secure DRM video courses with pre-lesson quizzes and 2-year verified certificates.
- **Launchpad** (\`/launchpad\`): Step-by-step account configuration.

Let me know which of these you would like to explore!`,
          route: matchedKnowledge?.route || "/launchpad",
          routeLabel: matchedKnowledge?.routeLabel || "Open Launchpad Overview",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        };
        setMessages(prev => [...prev, botReply]);
      }, 400);
    } finally {
      setBusy(false);
    }
  };

  const handlePromptClick = (p) => {
    handleSend(p);
  };

  const handleNavigate = (route) => {
    if (route) {
      navigate(route);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-xs">
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl flex flex-col h-[640px] max-h-[90vh] overflow-hidden font-sans">
        {/* ── Chatbot Header ── */}
        <div className="p-4 px-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
                  Platform Guide Assistant
                </h3>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <p className="text-xs text-slate-500">
                Interactive platform copilot for LaunchDeck, RealHUB & Colibri Studio
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onStartTour && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onClose();
                  onStartTour();
                }}
                className="hidden sm:inline-flex text-xs h-7 gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Rocket className="w-3 h-3" /> Start Visual Tour
              </Button>
            )}

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Close Guide"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── Message Feed ── */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 bg-slate-50/60 dark:bg-slate-950/40">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex items-start gap-3 ${
                m.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {m.sender === "bot" && (
                <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                  <Sparkles className="w-4 h-4" />
                </div>
              )}

              <div className="flex flex-col space-y-1 max-w-[85%] sm:max-w-[80%]">
                <div
                  className={`p-3.5 rounded-2xl text-xs sm:text-sm leading-relaxed ${
                    m.sender === "user"
                      ? "bg-blue-600 text-white rounded-tr-xs"
                      : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 rounded-tl-xs shadow-2xs whitespace-pre-line"
                  }`}
                >
                  {m.text}

                  {/* Direct Navigation Button if response mentions a specific module */}
                  {m.route && (
                    <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800">
                      <Button
                        size="sm"
                        onClick={() => handleNavigate(m.route)}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-7 px-3 gap-1.5 shadow-2xs"
                      >
                        <span>{m.routeLabel || "Open Module"}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                <span
                  className={`text-[10px] text-slate-400 px-1 ${
                    m.sender === "user" ? "text-right" : "text-left"
                  }`}
                >
                  {m.time}
                </span>
              </div>
            </div>
          ))}

          {busy && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-2xs">
                <Sparkles className="w-4 h-4 animate-spin" />
              </div>
              <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl rounded-tl-xs text-xs text-slate-500 shadow-2xs flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-ping" />
                <span>Thinking and preparing guidance…</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Suggested Questions Chips ── */}
        <div className="px-4 py-2 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center gap-1.5 overflow-x-auto scrollbar-none text-xs">
          <span className="text-[11px] font-semibold text-slate-400 shrink-0">
            Suggested:
          </span>
          {QUICK_PROMPTS.map((p, i) => (
            <button
              key={i}
              onClick={() => handlePromptClick(p)}
              className="px-2.5 py-1 rounded-full border border-slate-200 dark:border-slate-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-[11px] text-slate-600 dark:text-slate-300 whitespace-nowrap transition-colors shrink-0"
            >
              {p}
            </button>
          ))}
        </div>

        {/* ── Message Input Bar ── */}
        <div className="p-3 px-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex items-center gap-2 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 bg-slate-50/50 dark:bg-slate-800 focus-within:border-blue-500 focus-within:bg-white dark:focus-within:bg-slate-900 transition-all shadow-2xs"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about our platform (e.g. How to connect Google Drive, build a funnel, send SMS)..."
              className="flex-1 bg-transparent text-xs text-slate-800 dark:text-slate-100 outline-none placeholder:text-slate-400"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || busy}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-7 px-3 gap-1 shadow-2xs"
            >
              <span>Send</span>
              <Send className="w-3 h-3" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

